import { isErrorWithData } from '@/services/helpers';
import {
  usePostCreateOrganizationWebhooksSubscriptionMutation,
  useUpdateOrganizationWebhooksSubscriptionMutation,
  useTestOrganizationWebhooksSubscriptionMutation,
} from '@/services/organizations';
import {
  Box,
  Button,
  FormikSelectInputNative,
  FormikTextInput,
  FormikToggle,
  Modal,
  toast,
} from '@palmetto/palmetto-components';
import { Field, Formik, FormikHelpers } from 'formik';
import { useState, useCallback } from 'react';
import { Form } from 'react-router-dom';
import * as yup from 'yup';

import {
  WebhookEventTypeOrAggregate,
  WebhookEventType,
  WebhookHttpMethod,
  AggregateWebhookEventType,
  WEBHOOK_FAILURE_THRESHOLD,
} from 'types';

const webhookSubscriptionSchema = yup.object().shape({
  event: yup
    .string()
    .oneOf(Object.values({ ...AggregateWebhookEventType, ...WebhookEventType }))
    .required('This is a required field.'),
  endpointUrl: yup.string().required('This is a required field.'), //todo url suffix validation
  httpMethod: yup
    .string()
    .oneOf(Object.values({ ...WebhookHttpMethod }))
    .required('This is a required field.'),
  isEnabled: yup.boolean().required('This is a required field.'),
});

const defaultValues: {
  event: WebhookEventTypeOrAggregate;
  endpointUrl: string;
  httpMethod: WebhookHttpMethod;
  isEnabled: boolean;
} = {
  event: WebhookEventType.accountUpdated,
  endpointUrl: '',
  httpMethod: WebhookHttpMethod.POST,
  isEnabled: true, //TODO have to have successful test to set enabled?
};

export type WebhookSubscriptionSchema = yup.InferType<typeof webhookSubscriptionSchema>;
export const EditWebhookSubscriptionModal = ({
  registration,
  subscription,
  isOpen,
  handleClose,
}: {
  registration: any;
  subscription: any;
  isOpen: boolean;
  handleClose: () => void;
}) => {
  const [createSubscription] = usePostCreateOrganizationWebhooksSubscriptionMutation();
  const [updateSubscription] = useUpdateOrganizationWebhooksSubscriptionMutation();

  const isEditing = !!subscription;

  const [webhookTestResult, setWebhookTestResult] = useState<any>(null);

  const testResult = webhookTestResult ? (
    webhookTestResult.isSuccessful ? (
      <Box color="success">Test succeeded</Box>
    ) : (
      <Box color="danger">Test failed: {webhookTestResult.errorMessage}</Box>
    )
  ) : null;

  const [triggerTestWebhook, { isLoading: isTestingWebhook }] = useTestOrganizationWebhooksSubscriptionMutation();

  const testWebhook = useCallback(
    async (httpMethod: WebhookHttpMethod, endpointUrl: string) => {
      try {
        setWebhookTestResult(null);
        const response = await triggerTestWebhook({
          registrationId: registration?.id,
          body: {
            httpMethod,
            endpointUrl,
          },
        }).unwrap();
        setWebhookTestResult(response);
      } catch (error) {
        console.error('Error testing webhook:', error);
        setWebhookTestResult({ errorMessage: 'Failed to test webhook' });
      }
    },
    [triggerTestWebhook, registration?.id],
  );

  const handleSubmit = async (
    values: WebhookSubscriptionSchema,
    { resetForm }: FormikHelpers<WebhookSubscriptionSchema>,
  ) => {
    try {
      const body = {
        eventType: values.event,
        endpointUrl: values.endpointUrl,
        httpMethod: values.httpMethod,
        isEnabled: values.isEnabled,
      };

      if (isEditing) {
        await updateSubscription({
          registrationId: registration.id,
          subscriptionId: subscription.id,
          body,
        }).unwrap();
        toast.success('Subscription updated');
      } else {
        const response = await createSubscription({
          registrationId: registration.id,
          body,
        }).unwrap();

        const isDuplicate = response.messages?.some((msg) => msg.duplicate);
        if (isDuplicate) {
          toast.error('A subscription for this event type already exists');
          return;
        }
        toast.success('Subscription created');
      }
      setWebhookTestResult(null);
      resetForm();
      handleClose();
    } catch (e) {
      if (isErrorWithData(e)) {
        toast.error(e.data.message);
      } else {
        console.error(e);
        toast.error(isEditing ? 'Error updating subscription' : 'Error creating subscription');
      }
    }
  };

  let initialValues = defaultValues;
  if (registration && subscription) {
    initialValues = {
      event: subscription.eventType,
      endpointUrl: subscription.endpointUrl,
      httpMethod: subscription.httpMethod,
      isEnabled: subscription.isEnabled,
    };
  }

  return (
    <Formik
      validationSchema={webhookSubscriptionSchema}
      initialValues={initialValues}
      validateOnChange={false}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ isSubmitting, resetForm, submitForm, values }) => {
        return (
          <Form noValidate>
            <Modal
              isOpen={isOpen}
              maxWidth="4xl"
              ariaLabelledBy="addWebhookHeader"
              onDismiss={() => {
                resetForm();
                setWebhookTestResult(null);
                handleClose();
              }}
            >
              <Modal.Header
                id="addWebhookHeader"
                title="Add Webhook"
                onDismiss={() => {
                  resetForm();
                  setWebhookTestResult(null);
                  handleClose();
                }}
              />
              <Modal.Body background="secondary" childGap="lg">
                <Box childGap={{ base: 'lg', desktop: 'xl' }}>
                  <Field
                    id="event"
                    name="event"
                    label="Event"
                    component={FormikSelectInputNative}
                    options={Object.values({ ...AggregateWebhookEventType, ...WebhookEventType }).map((event) => ({
                      label: event,
                      value: event,
                    }))}
                    isRequired
                  />

                  <Field
                    id="httpMethod"
                    name="httpMethod"
                    label="HTTP Method"
                    component={FormikSelectInputNative}
                    options={Object.values(WebhookHttpMethod).map((method) => ({
                      label: method,
                      value: method,
                    }))}
                    isRequired
                  />

                  <Field
                    type="text"
                    label="Endpoint"
                    name="endpointUrl"
                    id="endpointUrl"
                    component={FormikTextInput}
                    isRequired
                    placeholder="/api/webhooks"
                  />

                  <Box>
                    <code>
                      {values.httpMethod} {registration?.hostUrl + values.endpointUrl}
                    </code>
                  </Box>

                  <Box childGap="2xs">
                    <Field type="toggle" label="Enabled" name="isEnabled" id="isEnabled" component={FormikToggle} />
                    <Box as="p" color="body-secondary" fontSize="sm">
                      Webhook subscriptions will be disabled after {WEBHOOK_FAILURE_THRESHOLD} consecutive failures.
                    </Box>
                  </Box>
                </Box>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  tone="neutral"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  onClick={() => {
                    resetForm();
                    setWebhookTestResult(null);
                    handleClose();
                  }}
                >
                  Cancel
                </Button>
                <Box flex="auto" />
                {testResult}
                <Button
                  onClick={() => testWebhook(values.httpMethod, values.endpointUrl)}
                  isLoading={isTestingWebhook}
                  isDisabled={isTestingWebhook || isSubmitting}
                  variant="secondary"
                >
                  Test
                </Button>
                <Button
                  variant="primary"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  type="submit"
                  onClick={submitForm}
                >
                  Save
                </Button>
              </Modal.Footer>
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};
